import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, X } from "lucide-react";
import { dropdownTriggerClasses, DropdownOption } from "./CustomDropdown";
import useAnchoredPanel, {
  anchoredPanelStyle,
  dismissKeyboard,
  sheetStyle,
} from "./useAnchoredPanel";
import OverflowTooltip from "./OverflowTooltip";
import useFitCount from "./useFitCount";

/**
 * A dropdown that takes several answers, for the fields where one is not
 * enough — food allergies, where a client may react to more than one thing.
 *
 * Its own component rather than a mode on `CustomDropdown`: that one now
 * carries search, "use what I typed", paged rendering and keyboard traversal,
 * all written around a single `string` value, and threading a second value
 * type through every one of those branches would put six working fields at
 * risk for the sake of one. What the two do share is the trigger's styling,
 * imported rather than copied, so they stay indistinguishable in a form row.
 *
 * The list stays open as options are ticked — closing after each one would
 * make picking three allergies three round trips.
 */

/** `gap-1.5` between the chips, in px — the measuring pass has to know it. */
const CHIP_GAP = 6;

/**
 * A value's identity for matching, ignoring case and edge whitespace — the
 * same rule `profileCodes` already matches specialization labels by. A
 * suggestion list and a value saved under an older wording of it can differ
 * only in case ("Prenatal Nutrition" against "Prenatal nutrition"), and that
 * must not read as two different answers, one ticked and one not.
 */
const identity = (value: string) => value.trim().toLowerCase();

export interface MultiSelectProps {
  options: DropdownOption[];
  /** The chosen values. Order is the order they were picked in. */
  values: string[];
  onChange: (values: string[]) => void;
  /** Fires when the list closes, however it closed — forms mark the field visited. */
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  /** Given a reason, a disabled control stays reachable so it can say it. */
  onDisabledClick?: () => void;
  invalid?: boolean;
  className?: string;
  /**
   * Tallest the option list can get before it scrolls, in px. Kept level with
   * `CustomDropdown`'s default — the two sit in the same forms and should open
   * to the same size.
   */
  maxHeight?: number;
  id?: string;
  "aria-labelledby"?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  values,
  onChange,
  onBlur,
  placeholder = "Select options",
  disabled = false,
  onDisabledClick,
  invalid = false,
  className = "",
  maxHeight = 320,
  id,
  ...aria
}) => {
  const [open, setOpen] = useState(false);
  // Which option the keyboard is on. -1 while nothing is highlighted.
  const [activeIndex, setActiveIndex] = useState(-1);

  const listId = `${React.useId()}-list`;

  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // `sheet`: a phone, where the list is laid at the foot of the screen rather
  // than hung off the field — see `useAnchoredPanel` for why, and
  // `CustomDropdown`, which is drawn the same way for the same reasons.
  const { rect, openUpward, sheet } = useAnchoredPanel(
    triggerRef,
    open,
    maxHeight
  );

  // A disabled control with something to say cannot carry the native
  // attribute: the browser fires no events at one, so the click that would ask
  // why never lands. It is marked `aria-disabled` instead.
  const softDisabled = disabled && !!onDisabledClick;

  /** Chosen values, keyed by identity so a case-only variant still matches. */
  const chosen = useMemo(
    () => new Set(values.map(identity)),
    [values]
  );

  /**
   * `options` plus any already-chosen value it does not hold — appended so a
   * value picked under an older list stays visible, both as a chip and as a
   * ticked row in the panel, rather than vanishing the moment a fresh fetch
   * replaces the suggestions under it. A field like Specializations takes
   * answers the suggestion list has never heard of, so this is not an edge
   * case for it.
   *
   * Matched by identity rather than exact text, so a value that differs from
   * an option only in case is folded into that option's own row instead of
   * getting a second, unchecked one beside it — the API's wording wins where
   * the two agree on everything else. What is genuinely new to the list
   * falls back to itself as its own label.
   */
  const panelOptions = useMemo<DropdownOption[]>(() => {
    const optionIdentities = new Set(
      options.map((option) => identity(option.value))
    );
    const seen = new Set<string>();
    const unknown: DropdownOption[] = [];
    values.forEach((value) => {
      const key = identity(value);
      if (optionIdentities.has(key) || seen.has(key)) return;
      seen.add(key);
      unknown.push({ value, label: value });
    });

    // And one row per value, whatever the caller handed over: a list is keyed
    // by its values, so the same value twice reconciles as one row and takes
    // the rows around it with it. See `CustomDropdown`, where a list of cities
    // carrying repeats is what made this worth refusing rather than trusting.
    const kept = new Set<string>();
    const listed = options.filter((option) => {
      if (kept.has(option.value)) return false;
      kept.add(option.value);
      return true;
    });

    return unknown.length === 0 && listed.length === options.length
      ? options
      : [...listed, ...unknown];
  }, [options, values]);

  /** Picked values in the order the list holds them, so chips read stably. */
  const chips = useMemo(
    () => panelOptions.filter((option) => chosen.has(identity(option.value))),
    [panelOptions, chosen]
  );

  // --- How many chips fit on the row ------------------------------------
  //
  // A chip is either shown whole or not shown at all, and what did not fit is
  // counted. The measuring is `useFitCount` — the Details tab's allergy line
  // reads the same way, off the same pass.

  const rowRef = useRef<HTMLSpanElement>(null);
  const chipRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const overflowRef = useRef<HTMLSpanElement>(null);

  /** Identifies the answers a measurement was taken of. */
  const chipsKey = useMemo(
    () => chips.map((option) => option.value).join(""),
    [chips]
  );

  const { measuring, shown, hiddenCount, tight } = useFitCount({
    rowRef,
    itemRefs: chipRefs,
    overflowRef,
    key: chipsKey,
    count: chips.length,
    gap: CHIP_GAP,
  });

  // Hovering the row lists every answer, once some of them are behind the
  // pill — see `OverflowTooltip`, which the Details tab opens the same way.
  const [hovered, setHovered] = useState(false);
  const tooltipOpen = hovered && hiddenCount > 0 && !open && !measuring;

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
    onBlur?.();
  }, [onBlur]);

  const openList = useCallback(() => {
    if (disabled) {
      onDisabledClick?.();
      return;
    }
    // The keyboard the field before this one left up, put away by the tap that
    // opened the list. This control has nothing to type into, so there is
    // nothing a keyboard could be wanted for — and a trigger that is a `div`
    // does not take the caret off that field by itself in every browser, which
    // is what left one up over an open sheet. See `dismissKeyboard`.
    if (sheet) dismissKeyboard();
    setOpen(true);
  }, [disabled, onDisabledClick, sheet]);

  /**
   * Ticking adds, unticking removes — and the list stays open either way.
   *
   * Removal matches by identity, not exact text: the row clicked may carry
   * the option list's own casing while `values` holds an older one, and it is
   * that stored entry — not a copy of what is on screen — that has to go.
   */
  const toggle = (value: string) => {
    const key = identity(value);
    onChange(
      chosen.has(key)
        ? values.filter((current) => identity(current) !== key)
        : [...values, value]
    );
  };

  const remove = (value: string) => {
    const key = identity(value);
    onChange(values.filter((current) => identity(current) !== key));
  };

  // A pointer press outside both the trigger and the panel dismisses.
  // `mousedown` rather than `click`, so the list is gone before a click lands
  // on whatever was underneath it.
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        close();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open, close]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        onDisabledClick?.();
      }
      return;
    }

    if (event.key === "Escape") {
      if (open) {
        event.stopPropagation();
        close();
      }
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => {
        if (panelOptions.length === 0) return -1;
        const next = current + step;
        // Wrap, so holding one arrow key cycles rather than dead-ending.
        if (next < 0) return panelOptions.length - 1;
        if (next >= panelOptions.length) return 0;
        return next;
      });
      return;
    }

    // Enter and Space tick without closing, which is the whole point of this
    // control — Enter would otherwise submit the form it sits in.
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (open && activeIndex >= 0 && panelOptions[activeIndex]) {
        toggle(panelOptions[activeIndex].value);
      } else if (!open) {
        setOpen(true);
      }
    }
  };

  /**
   * Where the list is drawn: hung under the trigger, or laid at the foot of
   * the screen as a sheet on a phone. See `sheetStyle` for what a sheet is
   * measured against, and why it is not the field.
   */
  // A sheet at the foot of the page, and no keyboard to work around: this
  // control has nothing to type into, and what the field before it left up is
  // put away as the list opens — see `openList`. `CustomDropdown` takes the
  // whole screen for the lists that do have something to type into; see
  // `takeoverStyle` for why that is the only reliable answer there.
  const panelStyle = sheet
    ? sheetStyle()
    : rect
      ? anchoredPanelStyle(rect, openUpward, maxHeight)
      : null;

  const panel =
    open && panelStyle
      ? createPortal(
          <>
            {/* Dimmed and deaf to drags, so the form cannot scroll about under
                a list that is no longer measured against it. Pressing it
                closes, through the outside-press handler below — the backdrop
                is outside the panel too. */}
            {sheet && (
              <div
                aria-hidden="true"
                className="fixed inset-0 z-[55] touch-none bg-[#0B1B1F]/25"
              />
            )}

            {/* Frame and list, two elements rather than one, so the sheet's
                Done row can sit under the options without scrolling away with
                them. Hung under a trigger there is nothing to pin and the
                frame is only a border. */}
            <div
              ref={panelRef}
              className={
                "z-[60] flex flex-col overflow-hidden bg-white " +
                (sheet
                  ? "rounded-t-2xl border-t border-line " +
                    // Clear of the home indicator. A sheet is always down on
                    // the glass now, so this is always worth keeping.
                    "pb-[env(safe-area-inset-bottom)] " +
                    "shadow-[0_-8px_32px_rgba(11,27,31,0.18)]"
                  : "rounded-md border border-line " +
                    "shadow-[0_8px_24px_rgba(11,27,31,0.12)]")
              }
              style={panelStyle}
            >
              {sheet && (
                // The bar every phone sheet wears, which is what says this is
                // a sheet laid over the form rather than the page having
                // jumped.
                <div
                  aria-hidden="true"
                  className="flex flex-shrink-0 justify-center pb-1 pt-2.5"
                >
                  <span className="h-1 w-9 rounded-full bg-[#E1E7EA]" />
                </div>
              )}

              <div
                id={listId}
                role="listbox"
                aria-multiselectable="true"
                aria-label={aria["aria-label"]}
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1 custom-scrollbar-overlay"
              >
              {panelOptions.length === 0 ? (
                <p className="px-4 py-3 text-center text-[13px] text-ink-subtle">
                  No options to choose from
                </p>
              ) : (
                panelOptions.map((option, index) => {
                  const isChosen = chosen.has(identity(option.value));
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={isChosen}
                      // Keeps focus on the trigger, so the control never loses
                      // its focus border while the list is being clicked through.
                      onMouseDown={(event) => event.preventDefault()}
                      // A tap fires this at whatever it landed on and leaves it
                      // there, so on a phone the row a finger brushed past
                      // would be the one wearing a highlight — see
                      // `CustomDropdown`, drawn the same way for the same
                      // reason.
                      onMouseEnter={
                        sheet ? undefined : () => setActiveIndex(index)
                      }
                      onClick={() => toggle(option.value)}
                      className={
                        "my-0.5 mx-2 flex w-[calc(100%-1rem)] items-center " +
                        "gap-2.5 rounded-md text-left transition-colors " +
                        // A row worth putting a thumb on, where a thumb is
                        // what is used.
                        (sheet ? "px-3 py-3 " : "px-3 py-2 ") +
                        // The ticked box is what says an answer is chosen, so
                        // where the keyboard or the pointer happens to be is a
                        // neutral grey. The brand colour on both would be two
                        // rows claiming to be the same thing.
                        (index === activeIndex
                          ? "bg-[#F1F4F6]"
                          : sheet
                            ? ""
                            : "hover:bg-[#F1F4F6]")
                      }
                    >
                      {/* A drawn box rather than a real checkbox: the row is
                          already the control, and a nested input would take the
                          click and the focus off it. */}
                      <span
                        aria-hidden="true"
                        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[3px] border transition-colors ${
                          isChosen
                            ? "border-brand bg-brand text-white"
                            : "border-line bg-white"
                        }`}
                      >
                        {isChosen && <Check className="h-3 w-3" strokeWidth={3} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className={`block ${sheet ? "text-[15px]" : "text-[14px]"} ${
                            isChosen ? "font-medium text-brand-dark" : "text-ink"
                          }`}
                        >
                          {option.label}
                        </span>
                        {option.subtitle && (
                          <span className="mt-0.5 block text-[12px] text-ink-subtle">
                            {option.subtitle}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })
              )}
              </div>

              {/* Ticking never closes this control — that is the whole of what
                  it is for — so on a phone, where there is no Escape key and
                  the field it belongs to is behind the sheet rather than
                  beside it, something has to say when the answer is finished.
                  A press outside still does the same thing. */}
              {sheet && (
                <button
                  type="button"
                  // Keeps focus on the trigger, the same way the options do.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={close}
                  className="w-full flex-shrink-0 border-t border-line px-4 py-3 text-center text-[15px] font-medium text-brand-dark"
                >
                  Done
                </button>
              )}
            </div>
          </>,
          document.body
        )
      : null;

  return (
    <div ref={triggerRef} className={`relative ${className}`}>
      {/* A div rather than a button: the chips inside carry their own remove
          buttons, and a button cannot contain one. `role="combobox"` and the
          tab stop are what keep it behaving as the control it looks like. */}
      <div
        id={id}
        role="combobox"
        tabIndex={disabled && !softDisabled ? -1 : 0}
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-invalid={invalid || undefined}
        aria-disabled={disabled || undefined}
        onClick={() => (open ? close() : openList())}
        // Only when the list is already shut — otherwise `close` reports it. A
        // refused control reports nothing: the field was never visited, so it
        // has no business turning red for being empty.
        onBlur={() => !open && !disabled && onBlur?.()}
        onKeyDown={handleKeyDown}
        className={`${dropdownTriggerClasses(invalid, disabled)} ${
          disabled ? "" : "cursor-pointer"
        }`}
        {...aria}
      >
        {/* One line, always. This control sits in a grid beside single-line
            fields, and a field that grew a row taller as answers were picked
            would push everything under it about. So the row shows the chips
            that fit whole and counts the rest. */}
        <span
          ref={rowRef}
          data-chip-row=""
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className={`flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden ${
            // The measuring render lays every chip out to be sized. It is
            // committed before the browser paints, so this is never seen.
            measuring && chips.length > 0 ? "invisible" : ""
          }`}
        >
          {chips.length === 0 ? (
            <span className="truncate text-ink-subtle">{placeholder}</span>
          ) : (
            <>
              {chips.slice(0, measuring ? chips.length : shown).map((option, index) => (
                <span
                  key={option.value}
                  ref={(node) => {
                    chipRefs.current[index] = node;
                  }}
                  data-chip=""
                  // Held at its full width, with no ceiling on it: a chip is
                  // shown to be read, and a name cut off halfway is the thing
                  // the count exists to avoid. A long one costs the row a
                  // place, which is the honest trade. The exception is a field
                  // too narrow for even one chip, where it has to give.
                  className={`inline-flex items-center gap-1 rounded-full bg-brand-soft py-0.5 pl-2.5 pr-1 text-[13px] text-brand-dark ${
                    tight ? "min-w-0" : "flex-shrink-0"
                  }`}
                >
                  {/* Truncation only ever bites in the tight case above —
                      everywhere else the chip is as wide as its name. */}
                  <span className="truncate">{option.label}</span>
                  {!disabled && (
                    <button
                      type="button"
                      aria-label={`Remove ${option.label}`}
                      // The chip sits inside the trigger, so its click has to
                      // stop there or removing one would also open the list.
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => {
                        event.stopPropagation();
                        remove(option.value);
                      }}
                      className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-brand-dark/70 transition-colors hover:bg-white/70 hover:text-brand-dark"
                    >
                      <X aria-hidden="true" className="h-3 w-3" strokeWidth={2.5} />
                    </button>
                  )}
                </span>
              ))}

              {/* What did not fit. Rendered through the measuring pass at its
                  widest, so the space kept for it is enough for the number it
                  ends up showing. */}
              {(measuring ? chips.length > 1 : hiddenCount > 0) && (
                <span
                  ref={overflowRef}
                  data-chip-overflow=""
                  className="inline-flex flex-shrink-0 items-center rounded-full bg-[#F1F4F6] px-2 py-0.5 text-[13px] font-medium text-ink-muted"
                >
                  +{measuring ? chips.length - 1 : hiddenCount}
                  <span className="sr-only"> more selected</span>
                </span>
              )}
            </>
          )}
        </span>

        {/* Read out with the field, since the row itself is only ever showing
            part of the answer. The count beside the label says the same thing
            for anyone who can see it. */}
        {chips.length > 0 && (
          <span className="sr-only">{chips.length} selected</span>
        )}

        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 flex-shrink-0 text-ink-muted transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </div>

      {panel}

      <OverflowTooltip
        anchorRef={triggerRef}
        open={tooltipOpen}
        items={chips.map((option) => option.label)}
        heading={`${chips.length} selected`}
      />
    </div>
  );
};

export default MultiSelect;
